/*
 UI COMPONENT: Classic Slider
 ---------------------------------------------------
 Horizontal scroll slider with prev/next navigation.
 Scrolls by `imagesDisplay` items at a time.
 Supports optional bullet pagination (auto-generated).
 Syncs state on manual swipe/scroll.

 Targets
 ---------------------------------------------------
 slides    — scrollable container wrapping all slides (children are the slides)
 prev      — previous button (auto-disabled at start)
 next      — next button (auto-disabled at end)
 bullets   — (optional) container for bullet navigation, auto-populated with buttons

 Values
 ---------------------------------------------------
 images-display  Number  (default: 2)  — visible slides at once

 Actions
 ---------------------------------------------------
 slider--classic#prev  — scroll to previous group
 slider--classic#next  — scroll to next group

 Usage
 ---------------------------------------------------
 <div data-controller="slider--classic" data-slider--classic-images-display-value="2">
   <div data-slider--classic-target="slides">
     <div>...</div>
     <div>...</div>
   </div>
   <button data-slider--classic-target="prev" data-action="slider--classic#prev">Prev</button>
   <button data-slider--classic-target="next" data-action="slider--classic#next">Next</button>
   <div data-slider--classic-target="bullets"></div>  <!-- optional -->
 </div>
 */
import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['slides', 'prev', 'next', 'bullets']
  static values = {
    imagesDisplay: { type: Number, default: 2 }
  }
  imagesLength
  imageWidth
  scrollIndex = 0
  scroller
  buttons = null

  connect() {
    this.scroller = this.slidesTarget
    const images = this.scroller.children
    if(!images.length) return

    this.imagesLength = images.length
    this.imageWidth = images[0].clientWidth
    this.scroller.addEventListener('scroll', (event) => this.#scrollHandler(event))

    if(this.hasBulletsTarget) {
      this.#createSliderNav(this.imagesLength)
    }

    this.#reset()
  }

  prev() {
    if(!this.scroller) return
    this.scrollIndex = Math.max(this.scrollIndex - 1, 0)
    this.#scroll()
  }

  next() {
    if(!this.scroller) return
    this.scrollIndex = Math.min(this.scrollIndex + 1, this.imagesLength - this.imagesDisplayValue)
    this.#scroll()
  }

  #scroll() {
    this.scroller.scrollTo({
      left: this.scrollIndex * this.imageWidth,
      behavior: 'smooth'
    })
    this.#updateButtonState()
  }

  #scrollHandler(event) {
    this.scrollIndex = Math.round(this.scroller.scrollLeft / this.imageWidth)
    this.#updateButtonState()
  }

  #updateButtonState() {
    this.prevTarget.disabled = this.scrollIndex === 0
    this.nextTarget.disabled = this.scrollIndex >= this.imagesLength - this.imagesDisplayValue

    if(this.hasBulletsTarget) {
      this.buttons.forEach((button, index) => button.toggleAttribute('active', index === this.scrollIndex))
    }
  }

  #reset() {
    this.scroller.scrollTo({
      left: 0,
    })
    this.#updateButtonState()
  }

  #buttonHandler(button, index) {
    button.index = index
    button.addEventListener('click', () => {
      this.scrollIndex = button.index
      this.#scroll()
      this.#updateButtonState()
    })
  }

  #createButton(label, active=false) {
    const button = document.createElement('button')
    if (active) button.setAttribute('active', '')

    const span = document.createElement('span')
    span.classList.add('sr-only')
    span.textContent = label
    button.appendChild(span)

    return button;
  }

  #createSliderNav(count=1) {
    let index = 0;
    while (index < count) {
      const button = this.#createButton(index, index === 0)
      this.bulletsTarget.appendChild(button)
      this.#buttonHandler(button, index)
      index++
    }
    this.buttons = this.bulletsTarget.querySelectorAll('button')
  }
}
